import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { companyApi, type AvvStatus } from '../lib/api';
import { useCompany } from './CompanyProvider';
import { Button } from './ui';

/**
 * AVV-Gate (Auftragsverarbeitung, Art. 28 DSGVO). Der Kunde ist Verantwortlicher
 * fuer die Daten seiner Geschaeftspartner; Saldify verarbeitet sie im Auftrag
 * (u.a. Sub-Prozessor Google/Gemini). Solange die aktuelle AVV-Fassung nicht
 * akzeptiert ist, wird die App fuer diese Firma blockiert.
 *
 * owner/admin koennen abschliessen; andere Rollen sehen einen Hinweis, dass der
 * Inhaber den AVV akzeptieren muss.
 */
export default function AvvGate({ children }: { children: ReactElement }): ReactElement {
  const { active } = useCompany();
  const [status, setStatus] = useState<AvvStatus | null>(null);
  const [checked, setChecked] = useState(false); // Checkbox „Ich schliesse den AVV ab"
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setStatus(null);
    // Zustimmung ist firmenbezogen: beim Mandantenwechsel nicht mitschleppen.
    setChecked(false);
    setErr(null);
    if (!active) return;
    companyApi.avvStatus(active.id).then(setStatus).catch(() => setStatus(null));
  }, [active]);

  // Noch nicht geladen ODER bereits akzeptiert -> App normal zeigen.
  // (Bei Ladefehler blockieren wir NICHT, damit ein API-Ausfall nicht die App sperrt.)
  if (!active || !status || status.accepted) return children;

  async function accept() {
    if (!active || !status) return;
    setBusy(true); setErr(null);
    try {
      await companyApi.acceptAvv(active.id, status.currentVersion);
      setStatus({ ...status, accepted: true, acceptedVersion: status.currentVersion, acceptedAt: new Date().toISOString() });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Konnte nicht gespeichert werden.');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <h2 className="mb-2 text-xl font-bold text-gray-900 dark:text-gray-100">
          Auftragsverarbeitungsvertrag (AVV)
        </h2>
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
          <p>
            Sie verarbeiten mit Saldify personenbezogene Daten Dritter (z. B. Ihrer Kunden und
            Lieferanten). Datenschutzrechtlich sind <strong>Sie der Verantwortliche</strong> und
            Saldify Ihr <strong>Auftragsverarbeiter</strong> (Art. 28 DSGVO). Zur Verarbeitung
            setzen wir Unterauftragsverarbeiter ein; die vollständige Übersicht finden Sie in der
            verlinkten <strong>Liste der Sub-Prozessoren</strong>.
          </p>
          <p>
            Bitte schließen Sie einmalig den Auftragsverarbeitungsvertrag ab. Er regelt Zweck,
            Umfang, technische/organisatorische Maßnahmen und die eingesetzten Unterauftragsverarbeiter.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/avv" target="_blank" rel="noopener" className="text-blue-600 underline hover:text-blue-700">AVV-Text lesen</Link>
            <Link to="/subprozessoren" target="_blank" rel="noopener" className="text-blue-600 underline hover:text-blue-700">Liste der Sub-Prozessoren</Link>
          </div>
        </div>

        {status.canAccept ? (
          <>
            <label className="mt-5 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1" />
              <span>Ich habe den AVV gelesen und schließe ihn als Verantwortlicher für diese Firma verbindlich ab.</span>
            </label>
            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
            <div className="mt-5 flex justify-end">
              <Button onClick={accept} busy={busy} disabled={!checked}>AVV abschließen</Button>
            </div>
          </>
        ) : (
          <p className="mt-5 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            Der AVV muss vom Inhaber/Administrator dieser Firma abgeschlossen werden. Bitte wenden Sie sich an die verantwortliche Person.
          </p>
        )}
      </div>
    </div>
  );
}
